<template>
	<v-row>
		<v-col>
			<page-header :is-editing="isEditing" title="Settings">
				<template v-slot:actions>
					<v-btn
							class="rounded-lg"
							color="info"
							depressed
							:disabled="isEditing"
							:large="$vuetify.breakpoint.mdAndUp"
							:small="$vuetify.breakpoint.xsOnly"
							@click="editSettings()"
					>
						<v-icon>mdi-pencil-outline</v-icon>
						<v-divider class="ms-n1 me-3" :dark="!isEditing" vertical></v-divider>
						Edit
					</v-btn>
				</template>
			</page-header>
			<v-container class="pa-0">
				<v-row>
					<v-col>
						<v-form v-model="formIsValid.basic" ref="basicForm" new-forms>

							<ui-box
									v-if="shouldShowSection('basic')"
									:form="$refs.basicForm"
									eager
									:is-editing="isEditing"
									new-forms
							>

								<form-title title="Basic Details"/>

								<form-divider inner/>

								<v-row>

									<v-col cols="12" sm="6">
										<v-text-field
												v-model="localPartner.name"
												class="rounded-lg"
												:disabled="true"
												label="Name"
												hide-details="auto"
												outlined
												persistent-hint
												:rules="rules.name"

										></v-text-field>
									</v-col>

									<v-col cols="12" sm="6">
										<v-text-field
												v-model="localPartner.name_legal"
												class="rounded-lg required"
												:disabled="!isEditing"
												label="Legal Name"
												hide-details="auto"
												outlined
												persistent-hint
												:rules="rules.name"
										></v-text-field>
									</v-col>

									<v-col cols="12" md="6">
										<v-text-field
												v-model="localPartner.address"
												class="rounded-lg required"
												:disabled="!isEditing"
												hide-details="auto"
												id="street-address"
												label="Street Address"
												ref="autocomplete"
												outlined
												:rules="rules.address"
										></v-text-field>
									</v-col>

									<v-col cols="12" sm="6">
										<v-text-field
												v-model="localPartner.address_line_2"
												class="rounded-lg"
												:disabled="!isEditing"
												hide-details="auto"
												label="Apt/Suite #"
												outlined
										></v-text-field>
									</v-col>

									<v-col cols="12" sm="6">
										<v-text-field
												v-model="localPartner.city"
												class="rounded-lg required"
												:disabled="!isEditing"
												hide-details="auto"
												label="City"
												outlined
												:rules="rules.city"
										></v-text-field>
									</v-col>

									<v-col cols="12" sm="6">
										<v-text-field
												v-model="localPartner.state"
												class="rounded-lg required"
												:disabled="!isEditing"
												hide-details="auto"
												label="State/Province"
												outlined
												:rules="rules.state"
										></v-text-field>
									</v-col>

									<v-col cols="12" sm="6">
										<v-text-field
												v-model="localPartner.zip_code"
												class="rounded-lg required"
												:disabled="!isEditing"
												hide-details="auto"
												label="Zip/Postal Code"
												outlined
												:rules="rules.zip_code"
										></v-text-field>
									</v-col>

									<v-col cols="12" sm="6" md="3">
										<v-select
												v-model="localPartner.country"
												class="rounded-lg required"
												:disabled="!isEditing"
												hide-details="auto"
												:items="countries"
												item-value="id"
												item-text="name"
												label="Country"
												outlined
												:rules="rules.country"
										></v-select>
									</v-col>

									<v-col cols="12" sm="6">
										<v-text-field
												v-model="localPartner.website"
												class="rounded-lg required"
												:disabled="!isEditing"
												hide-details="auto"
												label="Website"
												outlined
												:rules="rules.website"
										></v-text-field>
									</v-col>

									<v-col cols="12" sm="6">
										<vue-tel-input-vuetify
												ref="phone"
												v-model="localPartner.phone"
												append-icon="mdi-menu-down"
												class="rounded-lg required"
												clearable
												:defaultCountry="localPartner.country"
												:disabled="!isEditing"
												hide-details="auto"
												label="Phone Number"
												mode="international"
												outlined
												:preferred-countries="['us', 'ca']"
												:rules="rules.phone"
												@input="phoneInputEvent"
												@keydown="phoneKeyEvent"
										></vue-tel-input-vuetify>
									</v-col>
								</v-row>

							</ui-box>

						</v-form>

						<v-form v-model="formIsValid.advanced" ref="advancedForm" new-forms>

							<ui-box
									v-if="shouldShowSection('advanced')"
									:form="$refs.advancedForm"
									eager
									:is-editing="isEditing"
									new-forms
							>

								<form-title title="Advanced Details"/>

								<form-divider inner/>

								<v-row>
									<v-col cols="12" sm="6" md="4">
										<v-select
												v-model="localPartner.timezone"
												class="rounded-lg required"
												:disabled="!isEditing"
												hide-details="auto"
												:items="timezones"
												item-value="id"
												item-text="name"
												label="Timezone"
												outlined
										></v-select>
									</v-col>
									<v-col cols="12" sm="6" md="4">
										<v-select
												v-model="localPartner.distance_unit"
												class="rounded-lg required"
												:disabled="!isEditing"
												hide-details="auto"
												:items="distanceUnits"
												item-value="id"
												item-text="name"
												label="Distance Unit"
												outlined
										></v-select>
									</v-col>
									<v-col cols="12" sm="6" md="4">
										<v-select
												v-model="localPartner.currency_id"
												class="rounded-lg required"
												:disabled="!isEditing"
												hide-details="auto"
												:items="currencies"
												item-value="id"
												item-text="name"
												label="Currency"
												outlined
										></v-select>
									</v-col>
								</v-row>

								<v-row>
									<v-col cols="12" md="8">
										<v-row>
											<v-col
													md="6"
													sm="8"
											>
												<v-row no-gutters>
													<v-col>
														<v-text-field
																v-model="tempPrimaryColor"
																class="rounded-l-lg rounded-r-0"
																:disabled="!isEditing"
																hide-details="auto"
																label="Primary Color"
																outlined
																:rules="rules.hexColor"
																@change="updatePrimaryColor"
																@blur="updatePrimaryColor"
														></v-text-field>
													</v-col>
													<v-col cols="auto">

														<v-dialog
																v-model="primaryColorMenu"
																content-class="rounded-xl"
																:disabled="!isEditing"
																persistent
																scrollable
																width="320px"
														>

															<template v-slot:activator="{ on }">
																<v-btn
																		v-on="on"
																		class="px-2 rounded-l-0 rounded-r-lg"
																		color="secondary"
																		depressed
																		:disabled="!isEditing"
																		outlined
																		style="border-left: none; min-width: 56px;"
																		x-large
																>
																	<v-icon class="mx-0">mdi-palette</v-icon>
																</v-btn>
															</template>

															<v-card class="position-relative" rounded="xl">

																<v-card-title
																		class="d-flex justify-space-between ma-0 ma-md-4">
																	<div class="w-100">
																		<div
																				class="align-center d-flex justify-space-between">
																			<h6>Primary Color</h6>
																			<v-btn
																					class="mt-n1 me-n2"
																					color="black"
																					depressed
																					:disabled="!isEditing"
																					icon
																					@click="cancelPrimaryColor"
																			>
																				<v-icon>mdi-close</v-icon>
																			</v-btn>
																		</div>
																	</div>
																</v-card-title>

																<v-card-text class="text-body-2">
																	<v-color-picker
																			v-model="primaryColorPicked"
																			ref="colorPicker"
																			class="rounded-lg"
																			:disabled="!isEditing"
																			dot-size="20"
																			@input="(e) => tempPrimaryColor = e"
																	/>
																</v-card-text>

																<v-card-actions>
																	<div class="w-100">

																		<div
																				class="align-center d-flex justify-space-between">
																			<span></span>
																			<div class="d-flex justify-end">
																				<v-btn
																						class="me-2 me-md-4 rounded-lg"
																						color="grey darken-1"
																						depressed
																						:disabled="!isEditing"
																						:large="$vuetify.breakpoint.mdAndUp"
																						outlined
																						@click="cancelPrimaryColor"
																				>
																					Cancel
																				</v-btn>
																				<v-btn
																						class="rounded-lg"
																						color="primary"
																						depressed
																						:disabled="!isEditing"
																						:large="$vuetify.breakpoint.mdAndUp"
																						@click="handleAcceptPrimaryColor"
																				>
																					Accept
																				</v-btn>
																			</div>
																		</div>
																	</div>
																</v-card-actions>
															</v-card>
														</v-dialog>


													</v-col>
												</v-row>
											</v-col>
											<v-col class="d-flex" cols="auto" sm="4" md="6">
												<v-btn
														class="px-1 px-sm-2 rounded-lg"
														:color="tempPrimaryColor"
														depressed
														:disabled="!isEditing"
														:min-width="$vuetify.breakpoint.xsOnly
																? 48
																: ($vuetify.breakpoint.smOnly
																		? 64
																		: 92
																)"
														outlined
														x-large
												>
													<template v-if="$vuetify.breakpoint.xsOnly">B</template>
													<template v-else-if="$vuetify.breakpoint.smOnly">BTN</template>
													<template v-else>BUTTON</template>
												</v-btn>
												<v-btn
														class="px-1 px-sm-2 ms-2 rounded-lg"
														:color="tempPrimaryColor"
														depressed
														:disabled="!isEditing"
														:min-width="$vuetify.breakpoint.xsOnly
																? 48
																: ($vuetify.breakpoint.smOnly
																		? 64
																		: 92
																)"
														:style="{ color: textColor }"
														x-large
												>
													<template v-if="$vuetify.breakpoint.xsOnly">B</template>
													<template v-else-if="$vuetify.breakpoint.smOnly">BTN</template>
													<template v-else>BUTTON</template>
												</v-btn>
											</v-col>

										</v-row>
									</v-col>
								</v-row>

								<form-divider inner/>

								<template v-if="localPartner.hasCustomDomainConfig">

									<form-subsection title="Custom Domain DNS Settings">
										Enter the custom URL you want to configure. For example, "explore.yourwebsite.com". Common domains include: "Experience", "Go"
										<br><br>
										Configure any subdomain. This must be a domain you own, and the subdomain should not exist. All API calls sent should use this domain. (If your website is location at example.com, a good choice would be "experience.example.com" or "visit.example.com")
									</form-subsection>

									<v-row align="center">
										<v-col cols="12" md="6">
											<v-text-field
													v-model="domainName"
													class="rounded-lg"
													:disabled="domainNameDisabled"
													hide-details="auto"
													label="Custom Domain Name"
													outlined
													persistent-hint
													:error="domainNameError"
													:error-messages="domainNameErrorMessage"
													:hint="hintOnDomainName"
													:rules="rules.domainName"
											></v-text-field>
										</v-col>
										<v-col cols="auto" class="text-left d-flex align-center">
											<v-btn
													class="rounded-lg"
													color="primary"
													depressed
													:disabled="!isEditing || isSaving || !!savedDomainName"
													@click="addCustomDomainName"
											>
												<v-icon left v-if="domainNameButtonIcon">
													{{ domainNameButtonIcon }}
												</v-icon>
												{{ domainNameButtonStatus }}
											</v-btn>
										</v-col>
									</v-row>

									<template v-if="savedDomainName">
										<v-row no-gutters>
											<v-col cols="12" md="5">
												<v-text-field
														v-model="capitalizedAcmStatus"
														class="rounded-l-lg rounded-r-0"
														readonly
														:disabled="!isEditing"
														hide-details="auto"
														label="Domain Status"
														outlined
												></v-text-field>
											</v-col>
											<v-col cols="auto">
												<v-btn
														class="px-2 rounded-l-0 rounded-r-lg"
														color="primary"
														depressed
														:disabled="!isEditing"
														outlined
														style="border-left: none; min-width: 56px;"
														x-large
														@click="refreshCustomDomainName"
												>
													<v-icon class="mx-0">mdi-sync</v-icon>
												</v-btn>
											</v-col>
										</v-row>
									</template>

									<template v-if="domainName && localPartner.dns_target">
										<form-subsection title="DNS Setup" tag="h6">
											To complete the configuration, create the following <strong>CNAME</strong> DNS record in your DNS provider.
										</form-subsection>

										<v-row align="center">
											<v-col cols="12" md="5">
												<v-text-field
														v-model="cNameKey"
														class="rounded-lg"
														:disabled="!isEditing"
														readonly
														hide-details="auto"
														label="CNAME Key"
														outlined
												></v-text-field>
											</v-col>
											<v-col>
												<v-btn
														color="secondary"
														depressed
														fab
														outlined
														small
														:disabled="!isEditing"
														@click="copyToClipboard(cNameKey)"
												>
													<v-icon>
														mdi-content-copy
													</v-icon>
												</v-btn>
											</v-col>
											<v-col cols="12" md="5">
												<v-text-field
														v-model="localPartner.dns_target"
														class="rounded-lg"
														:disabled="!isEditing"
														readonly
														hide-details="auto"
														label="Value"
														outlined
												></v-text-field>
											</v-col>
											<v-col>
												<v-btn
														color="secondary"
														depressed
														fab
														outlined
														small
														:disabled="!isEditing"
														@click="copyToClipboard(localPartner.dns_target)"
												>
													<v-icon>
														mdi-content-copy
													</v-icon>
												</v-btn>
											</v-col>
										</v-row>

										<v-row>
											<v-col class="text-left" cols="auto">
												<v-btn
														class="rounded-lg"
														color="info"
														depressed
														:disabled="!isEditing"
														@click.prevent="openViewCustomDomainInstructions"
												>
													<v-icon>
														mdi-eye-outline
													</v-icon>
													View Instructions
												</v-btn>
											</v-col>
											<v-col class="text-left">
												<a :href="downloadCustomDomainInstructionsUrl"
													 :disabled="!isEditing"
												>
													<v-btn
															class="rounded-lg"
															color="info"
															depressed
															outlined
															:disabled="!isEditing"
													>
														<v-icon>
															mdi-download
														</v-icon>
														Download PDF Instructions
													</v-btn>
												</a>
											</v-col>
										</v-row>
									</template>

									<form-divider inner/>

								</template>

								<form-subsection title="Sales Tax">
									If you enter and save a tax rate here, it will automatically update all
									tax rates that are set on each of your channels and for any new
									channel(s) created, this tax rate will be the default.
								</form-subsection>

								<v-row>
									<v-col cols="12" md="6">
										<v-text-field
												v-model.number="localPartner.tax_rate"
												class="mt-4 mt-sm-0 rounded-lg"
												:disabled="!isEditing"
												hide-details="auto"
												label="Sales Tax Rate"
												outlined
												suffix=" %"
												type="number"
												@wheel.native.prevent
												ref="taxRateTextField"
										></v-text-field>
										<confirm-dialog
												cancelText="Cancel"
												confirmText="Save"
												description="Saving this new tax rate will override all tax rates defined on each of your channels. Are you sure you want to continue?"
												:open="showTaxRateAlert"
												title="Override Sales Tax Rate"
												@cancel="showTaxRateAlert = false"
												@confirm="confirmSaveChanges"
										></confirm-dialog>
									</v-col>
								</v-row>
							</ui-box>

						</v-form>

						<v-form v-model="formIsValid.images" ref="imagesForm" class="mb-8" new-forms>
							<ui-box
									v-if="shouldShowSection('image')"
									:additionalValidations="[hasImage]"
									:form="$refs.imagesForm"
									:is-editing="isEditing"
									new-forms
							>

								<form-title title="Images">
									Images for various placements throughout our ecosystem.
								</form-title>

								<form-divider inner/>

								<form-subsection
									caption="Recommended minimum size: 512 x 512 @ 2X"
									title="Add to Homescreen Icon"
								>
									Used where there is both vertical and horizontal space for clearly
									displaying your logo.
								</form-subsection>

								<v-row>
									<v-col>
										<v-row>
											<bw-image
													v-model="logo"
													:aspect-ratio="calculateAR(logo.cropperSettings.limitAR)"
													cover
													:disabled="!isEditing"
													:upload-assignment="'localPartner'"
													:upload-assignment-id="localPartner.id"
											></bw-image>
										</v-row>
									</v-col>
								</v-row>

								<form-subsection
									caption="Recommended minimum size: 256 x 64 @ 2X"
									title="Horizontal Logo"
								>
									Used mainly as the mobile pass header logo.
								</form-subsection>

								<v-row>
									<v-col>
										<v-row>
											<bw-image
													v-model="horizontalLogo"
													:aspect-ratio="4"
													contain
													:disabled="!isEditing"
													:upload-assignment="'localPartner'"
													:upload-assignment-id="localPartner.id"
											></bw-image>
										</v-row>
									</v-col>
								</v-row>

								<form-subsection
									caption="Recommended minimum size: 800 x 1200 @ 2X"
									title="Login/Experiences Background"
								>
									Seen in the mobile pass Login and Experience pages.
								</form-subsection>

								<v-row>
									<v-col>
										<v-row>
											<bw-image
													v-model="login"
													:aspect-ratio="calculateAR(login.cropperSettings.limitAR)"
													cover
													:disabled="!isEditing"
													:upload-assignment="'localPartner'"
													:upload-assignment-id="localPartner.id"
											></bw-image>
										</v-row>
									</v-col>
								</v-row>

								<form-subsection
									caption="Recommended minimum size: 1260 x 560 @ 2X"
									title="Email"
								>
									This image will be used as the header background in emails sent within
									Bandwango on your behalf.
								</form-subsection>

								<v-row>
									<v-col>
										<v-row>
											<bw-image
													v-model="email"
													:aspect-ratio="calculateAR(email.cropperSettings.limitAR)"
													cover
													:disabled="!isEditing"
													:upload-assignment="'localPartner'"
													:upload-assignment-id="localPartner.id"
											></bw-image>
										</v-row>
									</v-col>
								</v-row>

							</ui-box>
						</v-form>
					</v-col>
				</v-row>
			</v-container>
			<save-form
					v-if="isEditing"
					:refs="$refs"
					:is-saving="isSaving"
					new-forms
					:reset-form="true"
					save-text="Save"
					@cancel-action="cancelChanges"
					@save-action="saveChanges"
			></save-form>

			<bw-modal
					v-if="dialog.viewCustomDomainInstructions"
					title="Bandwango Pass URL Instructions"
					:width="1200"
			>
				<template #body>
					<div class="d-flex flex-fill justify-content-center mt-3">
						<template v-if="dialog.isViewInstructionsLoading">
							<v-row justify="center" align="center">
								<v-col cols="4" class="d-flex justify-center">
									<v-progress-circular
											indeterminate
											color="primary"
											size="50"
									></v-progress-circular>
								</v-col>
							</v-row>
						</template>
						<template v-if="customDomainInstructions">
							<div v-html="customDomainInstructions"></div>
						</template>
					</div>
				</template>
				<template #footer>
					<div class="align-center d-flex justify-space-between w-100">
						<span></span>
						<div class="d-flex justify-end">
							<v-btn
									class="me-2 me-md-4 rounded-lg"
									color="grey darken-1"
									depressed
									:large="$vuetify.breakpoint.mdAndUp"
									text
									@click="hideViewCustomDomainInstructions"
							>
								Cancel
							</v-btn>
						</div>
					</div>
				</template>
			</bw-modal>

		</v-col>
	</v-row>
</template>

<script>
import { calculateAR, copyToClipboard, helpers } from '../../../../Shared/helpers';
import { mapActions, mapGetters } from "vuex";
import ConfirmDialog from '@app/Shared/UI/ConfirmDialog.vue';

export default {
	name: "PartnerSettings",
	components: {
		ConfirmDialog
	},
	props: {
		partner: {
			type: Object,
			default: () => ({})
		},
		partnerTypes: {
			type: Array,
			default: () => []
		},
		partnerContractTypes: {
			type: Array,
			default: () => []
		},
		feeStructures: {
			type: Array,
			default: () => []
		},
		showSections: {
			type: Array,
			default: () => []
		},
		template: {
			type: Object,
			default: () => ({})
		},
		returnUrl: {
			type: String,
			default: ''
		},
		adminUsers: {
			type: Array,
			default: () => []
		},
	},
	data() {
		return {
			confirmed: false,
			countries: [
				{ id: "US", name: "United States" },
				{ id: "CA", name: "Canada" },
				{ id: "GB", name: "United Kingdom" },
			],
			currencies: [
				{ id: 1, name: "USD - $" },
				{ id: 2, name: "CAD - C$" },
				{ id: 3, name: "EUR - €" }, 
				{ id: 4, name: "GBP - £" },
			],
			distanceUnits: [
				{ id: "mi", name: "Miles - mi." },
				{ id: "km", name: "Kilometers - km." },
			],
			formIsValid: {
				basic: false,
				advanced: false,
				images: false
			},
			isEditing: false,
			isSaving: false,
			lastPartner: { ...this.partner },
			lastTaxRate: null,
			localPartner: this.partner,
			phoneIsValid: !!this.partner.phone,
			primaryColorMenu: false,
			primaryColorPicked: null,
			regions: [
				{ id: "Pacific/Rockies", name: "Pacific/Rockies" },
				{ id: "Central", name: "Central" },
				{ id: "Northeast", name: "Northeast" },
				{ id: "Southeast", name: "Southeast" },
			],
			rules: {
				address: [v => !!v || 'Street Address is required.'],
				city: [v => !!v || 'City is required.'],
				country: [v => !!v || 'Country is required.'],
				hexColor: [v => !v || /^#[0-9A-F]{6}$/i.test(v) || 'Invalid HEX color (e.g. #2a73c5)'],
				name: [v => !!v || 'Partner name is required.'],
				website: [
					v => {
						const protocolRegex = /^https?:\/\//;
						return protocolRegex.test(v) || 'Please include the whole website url including http(s)://'
					},
					v => {
						const fullRegex = /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b[-a-zA-Z0-9()@:%_+.~#?&\/=]*$/;
						return fullRegex.test(v) || 'Website url is invalid.'
					}
				],
				domainName: [
					v => {
						const fullRegex = /^[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b[-a-zA-Z0-9()@:%_+.~#?&\/=]*$/;
						return !v || fullRegex.test(v) || 'Domain name is invalid.'
					}
				],
				phone: [
					() => this.phoneIsValid || 'Please enter a valid phone.'
				],
				state: [v => !!v || 'State is required.'],
				zip_code: [v => !!v || 'Zip/Postal Code is required.'],
			},
			showTaxRateAlert: false,
			tempPrimaryColor: null,
			textColor: null,
			timezones: [
				{ id: "America/Anchorage", name: "Alaska Daylight Time" },
				{ id: "America/Los_Angeles", name: "Pacific Daylight Time" },
				{ id: "America/Phoenix", name: "Arizona" },
				{ id: "America/Denver", name: "Mountain Daylight Time" },
				{ id: "America/Chicago", name: "Central Daylight Time" },
				{ id: "America/New_York", name: "Eastern Daylight Time" },
				{ id: "Atlantic/Bermuda", name: "Atlantic Bermuda" },
				{ id: "Europe/Madrid", name: "Central European Time" },
				{ id: "Europe/Athens", name: "Eastern European Time" },
				{ id: "Europe/London", name: "Western European Time" },
			],
			savedDomainName: this.partner.checkout_website || this.partner.domain_name_requested,
			domainName: null,
			domainNameError: false,
			domainNameErrorMessage: null,
			domainNameStatus: null,
			dialog: {
				viewCustomDomainInstructions: false,
				isViewInstructionsLoading: false,
			},
			customDomainInstructions: null,
		}
	},
	beforeMount() {
		if(!this.localPartner.styles)
			this.$set(this.localPartner, 'styles', {});
		if(!this.localPartner.styles?.colors)
			this.$set(this.localPartner.styles, 'colors', {});

		// some might be stored as an empty array from old code
		if(!this.localPartner.styles?.colors?.primary
				|| Array.isArray(this.localPartner.styles.colors.primary))
			this.$set(this.localPartner.styles.colors, 'primary', {});
		if(!this.localPartner.media)
			this.$set(this.localPartner, 'media', []);
	},
	mounted() {
		const setColor = this.localPartner.styles.colors.primary.main || '#2a73c5';
		const setTextColor = this.localPartner.styles.colors.primary.backgroundText || '#ffffff';
		this.primaryColorPicked = setColor;
		this.tempPrimaryColor = setColor;
		this.textColor = setTextColor;
		this.$nextTick(() => {
			this.setUpAutoComplete();
			this.fixAddressPlaceholder();
		});
		this.lastTaxRate = this.localPartner.tax_rate;
		this.domainName = this.savedDomainName;
		this.domainNameStatus = !!this.savedDomainName && !!this.partner.dns_target ? 'success' : 'initial';
	},
	computed: {
		hasImage() {
			const locations = [this.logo, this.horizontalLogo, this.login, this.email];
			const images = locations
					.filter(i => !i.url.startsWith('https://via.placehold.co'));
			return images.length === locations.length;
		},
		logo: {
			get() {
				let m = this.localPartner.media.find(x => x.location === 'Logo');
				if(!m) {
					m = {
						location: 'Logo',
						name: 'Logo',
						url: '/images/placehold.co.png',
					}
				}
				m.cropperSettings = {
					limitAR: '1:1',
					x: 0,
					y: 0
				};
				return m;
			},
			set(v) {
				// auto setup icons with logo
				v.location = 'Logo';
				let i = this.localPartner.media.findIndex(x => x.location === v.location);
				if(i > -1)
					this.$set(this.localPartner.media, i, v);
				else
					this.localPartner.media.push(v);

				// icon 152
				const icon152 = { ...v, ...{ location: 'Icon152' } };
				icon152.scale_y = icon152.cropperSettings.scale_y = icon152.scale_x = icon152.cropperSettings.scale_x = 152 / 250;

				i = this.localPartner.media.findIndex(x => x.location === icon152.location);
				if(i > -1) {
					icon152.id = this.localPartner.media[i].id;
					this.$set(this.localPartner.media, i, icon152);
				} else {
					icon152.id = null;
					this.localPartner.media.push(icon152);
				}
				// icon 196
				const icon196 = { ...v, ...{ location: 'Icon196' } };
				icon196.scale_y = icon196.cropperSettings.scale_y = icon196.scale_x = icon196.cropperSettings.scale_x = 196 / 250;

				i = this.localPartner.media.findIndex(x => x.location === icon196.location);
				if(i > -1) {
					icon196.id = this.localPartner.media[i].id;
					this.$set(this.localPartner.media, i, icon196);
				} else {
					icon196.id = null;
					this.localPartner.media.push(icon196);
				}
			}
		},
		horizontalLogo: {
			get() {
				let m = this.localPartner.media.find(x => x.location === 'HorizontalLogo');
				if(!m) {
					m = {
						location: 'HorizontalLogo',
						name: 'Horizontal Logo',
						url: '/images/placehold.co.png',
					}
				}
				m.cropperSettings = {
					limitAR: 'NaN',
					x: 0,
					y: 0
				};
				return m;
			},
			set(v) {
				// auto setup icons with logo
				v.location = 'HorizontalLogo';
				let i = this.localPartner.media.findIndex(x => x.location === v.location);
				if(i > -1)
					this.$set(this.localPartner.media, i, v);
				else
					this.localPartner.media.push(v);
			}
		},
		login: {
			get() {
				let m = this.localPartner.media.find(x => x.location === 'Login');
				if(!m) {
					m = {
						location: 'Login',
						name: 'Login/Experiences Background',
						url: '/images/placehold.co.png',
					}
				}
				m.cropperSettings = {
					limitAR: '2:3',
					x: 0,
					y: 0
				};
				return m;
			},
			set(v) {
				// auto setup icons with logo
				v.location = 'Login';
				let i = this.localPartner.media.findIndex(x => x.location === v.location);
				if(i > -1)
					this.$set(this.localPartner.media, i, v);
				else
					this.localPartner.media.push(v);
			}
		},
		email: {
			get() {
				let m = this.localPartner.media.find(x => x.location === 'Email');
				if(!m) {
					m = {
						location: 'Email',
						name: 'Email',
						url: '/images/placehold.co.png',
					}
				}
				m.cropperSettings = {
					limitAR: '9:4',
					x: 0,
					y: 0
				};
				return m;
			},
			set(v) {
				// auto setup icons with logo
				v.location = 'Email';
				let i = this.localPartner.media.findIndex(x => x.location === v.location);
				if(i > -1)
					this.$set(this.localPartner.media, i, v);
				else
					this.localPartner.media.push(v);
			}
		},
		hintOnDomainName() {
			if (this.domainNameStatus === 'success' && this.localPartner.translatedAcmStatus !== 'success') {
				return 'Please reach out to our support team if you need to change your custom domain name.'
			} else {
				return '';
			}
		},
		domainNameButtonStatus() {
			if (!this.savedDomainName && this.domainNameStatus !== 'submitting') {
				return 'Submit';
			} else if (this.domainNameStatus === 'submitting') {
				return 'Retrieving...';
			} else {
				return 'Success!';
			}
		},
		domainNameButtonIcon() {
			if (!this.savedDomainName && this.domainNameStatus !== 'submitting') {
				return 'mdi-bullseye';
			}else {
				return '';
			}
		},
		domainNameDisabled() {
			return !this.isEditing || (!!this.savedDomainName && !!this.localPartner.dns_target);
		},
		capitalizedAcmStatus() {
			return !!this.localPartner.translatedAcmStatus ? this.localPartner.translatedAcmStatus.charAt(0).toUpperCase() + this.localPartner.translatedAcmStatus.slice(1) : '';
		},
		cNameKey() {
			return !!this.savedDomainName ? this.savedDomainName.split('.')[0] : '';
		},
		downloadCustomDomainInstructionsUrl() {
			return `/partner-portal/${this.partner.id}/settings/get-setup-instructions-pdf`;
		},
	},
	methods: {
		calculateAR,
		copyToClipboard,
		...mapActions('formState', ['setDirtyState']),
		cancelPrimaryColor() {
			this.primaryColorMenu = false;
			this.updateTextColor(this.primaryColor);
			this.tempPrimaryColor = this.localPartner.styles.colors.primary.main;
		},
		cancelChanges() {
			this.isEditing = false;
			this.localPartner = { ...this.lastPartner };
		},
		confirmSaveChanges() {
			this.confirmed = true;
			this.saveChanges();
		},
		editSettings() {
			this.isEditing = true;
			this.componentKey += 1;
		},
		fixAddressPlaceholder() {
			const $this = this;
			setTimeout(function () {
				const inputElement = document.getElementById('street-address');
				if(inputElement?.placeholder) {
					inputElement.placeholder = '';
				}
				else {
					$this.fixAddressPlaceholder();
				}
			}, 5);
		},
		handleAcceptPrimaryColor() {
			this.primaryColorMenu = false;
			this.updatePrimaryColor();
		},
		saveChanges() {
			if(this.lastTaxRate != this.localPartner.tax_rate && !this.confirmed) {
				this.showTaxRateAlert = true;
				return;
			}
			this.confirmed = false;
			this.lastTaxRate = this.localPartner.tax_rate;

			const partner = { ...this.localPartner };
			// managers should be only ids
			partner.managers = (partner.managers || []).map(x => x.id || x);
			this.isSaving = true;
			this.showTaxRateAlert = false;
			let url = `/partner-portal/${partner.id}/settings/save-settings`;
			axios.put(url, partner)
					.then(response => {
						this.partner.id = response.data.partner.id;
						this.localPartner = response.data.partner;
						this.lastPartner = { ...this.localPartner };
						this.savedDomainName = this.localPartner.checkout_website || this.localPartner.domain_name_requested;
						this.domainName = this.savedDomainName;

						window.EventBus.$emit('system-messages', [
							{
								message: this.partner.name + ' ' + (this.partner.id
										? 'updated'
										: 'created'),
								messageType: 'success',
								showOverlay: true,
								timeout: 2000
							}
						]);
						this.isSaving = false;
						this.isEditing = false;
					})
					.catch(error => {
						this.isSaving = false;
						this.isEditing = false;
						window.EventBus.$emit('system-messages', [
							{ message: error.message, messageType: 'error' }
						]);
					});
		},
		shouldShowSection(section) {
			return !this.showSections.length || this.showSections.includes(section);
		},
		setUpAutoComplete() {

			const options = {
				fields: [
					'name',
					'geometry',
					'place_id',
					'address_components',
					'formatted_address',
				],
			};

			this.autocomplete = new google.maps.places.Autocomplete(this.$refs.autocomplete.$el.querySelector('input'), options);
			this.autocomplete.addListener("place_changed", () => {
				const place = this.autocomplete.getPlace();
				let street, street_number;
				place.address_components.forEach(c => {
					if(c.types.includes('route')) {
						street = c.short_name;
					}
					if(c.types.includes('street_number')) {
						street_number = c.short_name;
					}
					if(c.types.includes('subpremise')) {
						this.localPartner.address_line_2 = c.long_name;
					}
					if(c.types.includes('postal_code')) {
						this.localPartner.zip_code = c.long_name;
					}
					if(c.types.includes('administrative_area_level_1')) {
						this.localPartner.state = c.long_name;
					}
					if(c.types.includes('locality')) {
						this.localPartner.city = c.long_name;
					}
					if(c.types.includes('country')) {
						this.localPartner.country = c.short_name;
					}
				})
				this.localPartner.address = `${street_number} ${street}`;
				this.localPartner.latitude = place.geometry.location.lat();
				this.localPartner.longitude = place.geometry.location.lng();
				this.localPartner.google_place_id = place.place_id;
				this.localPartner.address_components = place.address_components;
			});

		},
		taxRateAlert() {
			this.showTaxRateAlert = true;
		},
		phoneInputEvent(formattedNumber, { valid }) {
			// Only flip the form validity to false if NOT valid, do nothing on valid = true
			// if(formattedNumber && !valid) this.formIsValid = false;
			// For field validation rule
			this.phoneIsValid = valid;
		},
		phoneKeyEvent(e) {
			const validCharsRegex = new RegExp(/^[0-9 +\-()]*$/);

			// Allow Control+V or Command+V for pasting
			const isPasteOperation = (e.key === 'v' || e.key === 'V') && (e.ctrlKey || e.metaKey);

			if(!validCharsRegex.test(e.key) && ![
				'ArrowLeft',
				'ArrowRight',
				'Backspace',
				'Delete',
				'Tab'
			].includes(e.key) && !isPasteOperation) {
				e.preventDefault();
			}
		},
		updatePrimaryColor() {
			this.primaryColorPicked = this.tempPrimaryColor;
			this.localPartner.styles.colors.primary.main = this.tempPrimaryColor;
			this.localPartner.styles.colors.primary.backgroundText = helpers.getContrastTextColor(this.primaryColor);
		},
		updateTextColor(color) {
			this.textColor = helpers.getContrastTextColor(color);
		},
		addCustomDomainName() {
			const isValid = this.$refs.advancedForm.validate();
			if (!isValid) return;

			const partner = { ...this.localPartner };
			this.isSaving = true;
			this.domainNameStatus = 'submitting';

			const url = `/partner-portal/${partner.id}/settings/add-custom-domain`;
			axios.post(url, {	domainName: this.domainName	})
					.then(response => {
						this.savedDomainName = response.data.checkoutWebsite || response.data.domainNameRequested;
						this.domainName = this.savedDomainName;
						this.localPartner.checkout_website = response.data.checkoutWebsite;
						this.localPartner.domain_name_requested = response.data.domainNameRequested;
						this.localPartner.dns_target = response.data.dnsTarget;
						this.localPartner.acm_status = response.data.acm_status;
						this.localPartner.translatedAcmStatus = response.data.translatedAcmStatus;
						this.domainNameStatus = 'success';
						window.EventBus.$emit('system-messages', [
							{
								message: response.data.message,
								messageType: 'success',
								showOverlay: true,
							}
						]);
					})
					.catch(error => {
						this.domainNameError = true;
						this.domainNameErrorMessage = 'This custom domain is not available! Please try another version.'
						this.domainNameStatus = 'failed';
					})
					.finally(() => {
						this.isSaving = false;
					});
		},
		refreshCustomDomainName() {
			const partner = { ...this.localPartner };
			this.isSaving = true;
			const url = `/partner-portal/${partner.id}/settings/refresh-custom-domain`;
			axios.put(url, { domainName: this.domainName	})
					.then(response => {
						this.savedDomainName = response.data.checkoutWebsite || response.data.domainNameRequested;
						this.domainName = this.savedDomainName;
						this.localPartner.checkout_website = response.data.checkoutWebsite;
						this.localPartner.domain_name_requested = response.data.domainNameRequested;
						this.localPartner.dns_target = response.data.dnsTarget;
						this.localPartner.acm_status = response.data.acm_status;
						this.localPartner.translatedAcmStatus = response.data.translatedAcmStatus;
						window.EventBus.$emit('system-messages', [
							{
								message: response.data.message,
								messageType: 'success',
								showOverlay: true,
							}
						]);
					})
					.catch(error => {
						window.EventBus.$emit('system-messages', [
							{ message: error.message, messageType: 'error' }
						]);
						this.domainNameStatus = 'failed';
					})
					.finally(() => {
						this.isSaving = false;
					});
		},
		openViewCustomDomainInstructions() {
			this.dialog.viewCustomDomainInstructions = true;
			this.dialog.isViewInstructionsLoading = true;
			const url = `/partner-portal/${this.partner.id}/settings/get-setup-instructions`;
			axios.get(url)
					.then(response => {
						this.customDomainInstructions = response.data;
					})
					.catch(error => {
						window.EventBus.$emit(
								'system-messages',
								[{ message: error.message, messageType: 'error' }]
						);
					})
					.finally(() => {
						this.dialog.isViewInstructionsLoading = false;
					});
		},
		hideViewCustomDomainInstructions() {
			this.dialog.viewCustomDomainInstructions = false;
			this.customDomainInstructions = null;
		},
	},
	watch: {
		tempPrimaryColor: function (newVal) {
			this.updateTextColor(newVal);
		},
		domainName: function (newVal) {
			this.domainNameError = false;
			this.domainNameErrorMessage = null;
		}
	}
};
</script>

<style lang="scss">
@use 'sass:color';

.wiz-btn-icon {
	background-color: var(--v-info-darken1) !important;
	border-radius: 50% !important;
	color: white !important;
	font-size: 28px !important;
	margin: 0 0.75rem 0 -1.5rem !important;
	padding: 0.75rem !important;
}
</style>
